import React from 'react';
import PropTypes from 'prop-types';
import Icons from '../utils/icons';
import { validators_deprecated } from "../utils/validators";
import { neo_eval } from "../utils/eval";

import NumberInput from './inputs/number';
import RadioGroup, { RadioButton } from './inputs/radioGroup';
import GroupInput from './inputs/groupInput';
import InputGroup from './inputs/inputGroup';
import DetailsInput from './inputs/detailsInput';
import { DateInput, CountrySelectInput, BirthDateInput } from './ui';

function DividerInput() {
    return null
}

const INPUTS = {
    number: NumberInput,
    radio_group: RadioGroup,
    group: GroupInput,
    radio: RadioButton,
    details: DetailsInput,
    date: DateInput,
    // Compatibility for old weez
    birthdate: BirthDateInput,
    country: CountrySelectInput,
    divider: DividerInput
};

export const TYPES = {
    int: x => parseInt(x),
    float: x => parseFloat(x),
    string: x => x.toString(),
    bool: x => Boolean(x),
    date: x => {
        if(typeof x === 'string') {
            return x;
        }

        // Airbnb lib returns moment dates...
        return x.toISOString();
    },
};

export default function NeoInput({ type='text', label=null, error=null, validate=true, validator, placeholder, className='', valueType ='', onChange=()=>{}, showLabel=true, isInGroup=false, value, description=null, active=true, props, ...rest }) {
    const [internalError, setError] = React.useState(error);

    value = neo_eval(value, valueType);
    label = neo_eval(label, 'string');

    //Set input type according to type props
    const internal_types = {
        "barcode": "tel",
    };

    const Component = INPUTS[type] || InputGroup;

    React.useEffect(() => {
        setError(error);
    }, [error]);

    const castType = React.useCallback((value) => {
        let returnType = TYPES[valueType] || TYPES[type];
        if(returnType){
            let val = returnType(value);
            if(val !== undefined && (!isNaN(val) || typeof val === 'string')){
                value = val;
            }
        }
        return value;
    }, [valueType]);

    const internalValidate = React.useCallback(({ target: { value }}) => {
        if(!validate) {
            return;
        }

        //Pick the prop validator if existing or look into the default validator from validators.js
        let _validator = null;
        if(Component.Validator) {
            _validator = Component.Validator;
        }

        if(validators_deprecated[type]) {
            _validator = validators_deprecated[type];
        }

        if(validator) {
            _validator = validator;
        }

        if(! _validator){
            // if we do not have validator on field ( because not required and no specific validator)
            return true;
        }

        value = castType(value);

        let validation = _validator(value, {...rest, type: valueType, }); // validate with rest of the props
        if (validation.result === false) {
            return setError(validation.message);
        }
        if (error) {
            setError(null);
        }
        return validation.result;
    }, [ validator, error, type, validate, Component, rest, castType, valueType]);

    const change = React.useCallback((val) => {
        let value = val;
        if(val?.target){
            value = val.target.value;
        }

        // Handle checkbox cases
        if(val?.target && val?.target.type === 'checkbox') {
            value = val.target.checked ? 1 : 0;
        }

        if(internalError) {
            setError(null);
        }

        value = [null, undefined].includes(value) ? value : castType(value);
        onChange(value);
    }, [onChange, internalError, castType]);

    let OuterComponent = 'div';
    let outerComponentProps = { className: `${className} wz-content wz-neo-input ${Component === DividerInput || type === 'checkbox' ? 'wz-neo-input-only-label' : ''}` };

    if(isInGroup && ["radio_group", "radio"].includes(type.toString())){
        OuterComponent = React.Fragment;
        outerComponentProps = {};
    }

    return (
        <OuterComponent {...outerComponentProps}>
            <InputLabel showLabel={showLabel} label={label} className={className}/>
            <InputDescription description={description} className={className}/>
            <Component
                className={className}
                type={internal_types[type] || type}
                placeholder={placeholder}
                label={label}
                onBlur={internalValidate}
                onChange={change}
                value={value}
                active={active.toString()}
                icon={Icons[type] || null}
                error={error || internalError}
                {...rest}
            />
            {
                !isInGroup &&
                <InputError error={error || internalError}/>
            }
        </OuterComponent>
    );
}

export function InputLabel({ showLabel, label, className='' }){
    if(!showLabel || !label){
        return null
    }
    return(
        <label className={`${className} wz-neo-input-label`}>
            {label}
        </label>
    )
}

export function InputDescription({ description, className='' }){
    if(!description){
        return null
    }

    return(
        <div>
            <label className={`${className} wz-neo-input-label-description`}>
                {description}
            </label>
        </div>
    )
}

export function InputError({ error }){
    return(
        <div className="wz-neo-input-error">
            <small>
                {error}
            </small>
        </div>
    )
}

NeoInput.propTypes = {
    type: PropTypes.string,
    label: PropTypes.string,
    validator: PropTypes.func,
    error: PropTypes.string,
    placeholder: PropTypes.string,
    showLabel: PropTypes.bool,
    description: PropTypes.string,
};

NeoInput.defaultProps = {
    validator: null,
    label: null,
    error: null,
    placeholder:null,
    showLabel: true,
    type: "text",
    description: null,
};
